import * as Sentry from '@sentry/react';
import { get, mergeWith } from 'lodash';
import React, {
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';

import { TextField } from 'Containers/App/types';
import { ContentfulType } from 'ContentfulDefaults/ContentfulType';
import { getChineseDefaults } from 'ContentfulDefaults/defaults/chinese/chineseDefaults';
import { getEnglishDefaults } from 'ContentfulDefaults/defaults/english/englishDefaults';
import { getSpanishDefaults } from 'ContentfulDefaults/defaults/spanish/spanishDefaults';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { AlexGoLanguage } from 'Utils/apiTypes';
import { BACKEND_HOST, BASE_PATH, MTM_PATH, PROFILE_PATH, RESULT_PATH, WELCOME_PATH } from 'Utils/urls';

export type { AlexGoLanguage };

export enum CLIENT_MODEL_CONTENT_TYPES {
  logo = 'logo',
  display_name = 'display_name',
  eligibility_section = 'eligibility_section',
  top_level_navigation = 'top_level_navigation',
  unified_landing_page = 'unified_landing_page',
  profile_section = 'profile_section',
  claims_summary_bar = 'claims_summary_bar',
  tool_tips = 'tool_tips',
  people_like_you = 'people_like_you',
  health_section = 'health_section',
  health_details_section = 'health_details_section',
  benefits_section = 'benefits_section',
  benefits_section_add = 'benefits_section_add',
  benefits_section_additional = 'benefits_section_additional',
  benefits_section_dental = 'benefits_section_dental',
  benefits_section_vision = 'benefits_section_vision',
  benefits_section_hospital_indemnity = 'benefits_section_hospital_indemnity',
  benefits_section_pet = 'benefits_section_pet',
  benefits_section_cancer = 'benefits_section_cancer',
  benefits_section_commuter = 'benefits_section_commuter',
  benefits_section_medical_fsa = 'benefits_section_medical_fsa',
  benefits_section_fsa = 'benefits_section_fsa',
  benefits_section_life = 'benefits_section_life',
  benefits_section_retirement = 'benefits_section_retirement',
  benefits_section_disability = 'benefits_section_disability',
  review_section = 'review_section',
  plan_customizations = 'plan_customizations',
  fbs_tokens = 'fbs_tokens',
  hsa = 'hsa',
  spousal_plan_comparison = 'spousal_plan_comparison',
  call_to_action = 'call_to_action',
  alex_intro_page = 'alex_intro_page',
  moments_that_matter = 'moments_that_matter',
  benefits_section_ltc = 'benefits_section_ltc',
  profile_section_addtl = 'profile_section_addtl',
  alex_go_review_note = 'alex_go_review_note',
}

// We may want to actually type this specifically as an object with the keys '/', '/profile', and '/results'
const ROUTE_TO_QUERY_MAP: Record<string, string[]> = {
  [WELCOME_PATH]: [
    CLIENT_MODEL_CONTENT_TYPES.logo,
    CLIENT_MODEL_CONTENT_TYPES.display_name,
    CLIENT_MODEL_CONTENT_TYPES.eligibility_section,
    CLIENT_MODEL_CONTENT_TYPES.top_level_navigation,
    CLIENT_MODEL_CONTENT_TYPES.unified_landing_page,
    CLIENT_MODEL_CONTENT_TYPES.profile_section,
    CLIENT_MODEL_CONTENT_TYPES.claims_summary_bar,
    CLIENT_MODEL_CONTENT_TYPES.call_to_action,
    CLIENT_MODEL_CONTENT_TYPES.alex_intro_page,
  ],
  [PROFILE_PATH]: [
    CLIENT_MODEL_CONTENT_TYPES.tool_tips,
    CLIENT_MODEL_CONTENT_TYPES.people_like_you,
    CLIENT_MODEL_CONTENT_TYPES.health_section,
    CLIENT_MODEL_CONTENT_TYPES.health_details_section,
    CLIENT_MODEL_CONTENT_TYPES.profile_section_addtl,
    CLIENT_MODEL_CONTENT_TYPES.call_to_action,
  ],
  [RESULT_PATH]: [
    CLIENT_MODEL_CONTENT_TYPES.benefits_section,
    CLIENT_MODEL_CONTENT_TYPES.benefits_section_add,
    CLIENT_MODEL_CONTENT_TYPES.benefits_section_additional,
    CLIENT_MODEL_CONTENT_TYPES.benefits_section_dental,
    CLIENT_MODEL_CONTENT_TYPES.benefits_section_vision,
    CLIENT_MODEL_CONTENT_TYPES.benefits_section_hospital_indemnity,
    CLIENT_MODEL_CONTENT_TYPES.benefits_section_pet,
    CLIENT_MODEL_CONTENT_TYPES.benefits_section_cancer,
    CLIENT_MODEL_CONTENT_TYPES.benefits_section_commuter,
    CLIENT_MODEL_CONTENT_TYPES.benefits_section_medical_fsa,
    CLIENT_MODEL_CONTENT_TYPES.benefits_section_life,
    CLIENT_MODEL_CONTENT_TYPES.benefits_section_retirement,
    CLIENT_MODEL_CONTENT_TYPES.benefits_section_disability,
    CLIENT_MODEL_CONTENT_TYPES.benefits_section_fsa,
    CLIENT_MODEL_CONTENT_TYPES.review_section,
    CLIENT_MODEL_CONTENT_TYPES.plan_customizations,
    CLIENT_MODEL_CONTENT_TYPES.fbs_tokens,
    CLIENT_MODEL_CONTENT_TYPES.hsa,
    CLIENT_MODEL_CONTENT_TYPES.spousal_plan_comparison,
    CLIENT_MODEL_CONTENT_TYPES.benefits_section_ltc,
    CLIENT_MODEL_CONTENT_TYPES.alex_go_review_note,
    CLIENT_MODEL_CONTENT_TYPES.call_to_action,
  ],
  [MTM_PATH]: [
    CLIENT_MODEL_CONTENT_TYPES.logo,
    CLIENT_MODEL_CONTENT_TYPES.display_name,
    CLIENT_MODEL_CONTENT_TYPES.top_level_navigation,
    CLIENT_MODEL_CONTENT_TYPES.review_section,
    CLIENT_MODEL_CONTENT_TYPES.moments_that_matter,
    CLIENT_MODEL_CONTENT_TYPES.call_to_action,
  ],
};

// Contentful locale for each ALEX Go language. Adding an entry here is not enough
// on its own — the locale must exist in the Contentful space too. A content
// request for a locale Contentful doesn't have fails (not a 404), which sends the
// user to the error page, so confirm the locale before enabling it for a customer.
export const LOCALE_BY_LANGUAGE: Record<AlexGoLanguage, string> = {
  english: 'en-US',
  spanish: 'es-US',
  chinese: 'zh-CN',
  haitian_creole: 'ht-HT',
};

const LANGUAGE_BY_LOCALE: Record<string, AlexGoLanguage> = Object.fromEntries(
  Object.entries(LOCALE_BY_LANGUAGE).map(([language, locale]) => [locale, language as AlexGoLanguage]),
);

// Bundled fallbacks, used only when the backend can't serve a section (404).
// Languages without their own tree fall back to English rather than shipping
// untranslated copy under a translated locale.
const DEFAULTS_BY_LOCALE: Record<string, (field: keyof ContentfulType) => unknown> = {
  'en-US': getEnglishDefaults,
  'es-US': getSpanishDefaults,
  'zh-CN': getChineseDefaults,
};

// `lang` attribute for each locale, for screen readers and hyphenation.
const HTML_LANG_BY_LOCALE: Record<string, string> = {
  'es-US': 'es',
  'zh-CN': 'zh',
  'ht-HT': 'ht',
};

export interface ITextContext {
  textMap: ContentfulType | Record<string, unknown>;
  retrieveContentfulData: <T>(key: string | TextField, defaultValue?: T) => T;
  isLoading: boolean;
  locale: string;
  setLocale: Dispatch<SetStateAction<string>>;
  isError: boolean;
  initialMap?: ContentfulType;
  // Union of languages enabled across the active + upcoming publications, merged
  // from bootstrap. Empty when nothing is configured (the language selector then
  // does not render).
  enabledLanguages: AlexGoLanguage[];
}

export const useTextContext = (): ITextContext => useContext(TextContext);

export const TextContext = React.createContext<ITextContext>({
  textMap: {},
  isLoading: false,
  locale: 'en-US',
  setLocale: () => null,
  // @ts-expect-error (we don't want a default value here, but are required to set one)
  retrieveContentfulData: () => '',
  isError: false,
  enabledLanguages: [],
});

export function retrieveContentfulDataFromMap<T>(
  map: ContentfulType | Record<string, unknown>,
  key: string,
  defaultValue?: T,
): T {
  return get(map, key) || defaultValue;
}

// Customizer for lodash `mergeWith`: replace arrays wholesale instead of combining
// them element-by-element. Contentful rich text is an array of nodes, and translations
// split a sentence into different nodes, so an index-merge would splice one language's
// nodes and marks into another's. Non-arrays fall through to the default merge, so an
// untranslated field still falls back to the English base.
const replaceArraysOnMerge = (_objValue: unknown, srcValue: unknown): unknown =>
  Array.isArray(srcValue) ? srcValue : undefined;

interface TextProviderProps {
  children: ReactNode;
  initialMap?: ContentfulType | Record<string, never>;
  // Languages enabled across the active + upcoming publications, from bootstrap
  // `conversation.languages`. Drives the language selector.
  enabledLanguages?: AlexGoLanguage[];
}

export const TextProvider = ({
  initialMap,
  children,
  enabledLanguages = [],
}: TextProviderProps): ReactElement => {
  const getInitialLocale = () => {
    try {
      return localStorage.getItem('locale') || 'en-US';
    } catch (error) {
      console.error('Failed to get locale from localStorage:', error);
      return 'en-US';
    }
  };

  const [locale, setLocale] = useState(getInitialLocale);
  // The language the page is showing. It trails `locale` while a newly chosen language
  // loads, so the page keeps the current language on screen instead of going blank, then
  // swaps to the new one in a single step.
  const [displayedLocale, setDisplayedLocale] = useState(locale);
  const latestLocale = useRef(locale);
  // Content already fetched, kept per locale so switching back and forth doesn't
  // refetch. Keyed by locale so a new language needs no branch of its own. Seed the
  // starting locale with the bootstrap map so the first render has content before
  // any fetch resolves.
  const [localeMaps, setLocaleMaps] = useState<Record<string, Record<string, any>>>(
    initialMap && Object.keys(initialMap).length ? { [locale]: initialMap } : {},
  );
  const [isLoading, setIsLoading] = useState(false);
  const [visited, setVisited] = useState<string[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const { useContentfulV2 } = useFeatureFlagContext();

  // What the app renders: the current locale layered over English, so the new language
  // fully replaces the old (mergeWith replaces rich-text arrays wholesale — no mixed
  // nodes on a switch) while any field the locale hasn't translated falls back to
  // English. That fallback only exists if English was loaded earlier in the session:
  // nothing fetches English for a session that starts in another language, so there an
  // untranslated field renders empty. English itself has nothing to fall back to, so
  // skip the self-merge and use its map directly. Memoized so the deep merge only
  // re-runs on locale/content change.
  const textMap: ContentfulType | Record<string, any> = useMemo(() => {
    const current = localeMaps[displayedLocale] || {};
    if (displayedLocale === 'en-US') return current;
    return mergeWith({}, localeMaps['en-US'] || {}, current, replaceArraysOnMerge);
  }, [localeMaps, displayedLocale]);

  // Correct a locale the customer doesn't enable — persisted in localStorage from
  // another customer (the key is shared across clients on this host), or left over
  // after their config changed. This is the only place locale is reconciled against
  // the enabled set: it lives here rather than in the language selector because the
  // selector is unmounted on most routes, so a deep link would otherwise keep
  // serving a language the customer never enabled.
  //
  // Only act once the enabled set is known — while it's empty (e.g. bootstrap still
  // loading after login) keep the persisted choice so we don't clobber it.
  useEffect(() => {
    if (enabledLanguages.length === 0) return;

    const language = LANGUAGE_BY_LOCALE[locale];
    if (language && enabledLanguages.includes(language)) return;

    // Prefer the customer's first enabled language over hardcoding English —
    // nothing guarantees English is in the set. Skip any language this build has no
    // locale for, which can happen when platform-api adds one before the frontend
    // catches up.
    const fallback = enabledLanguages.find((enabled) => enabled in LOCALE_BY_LANGUAGE);
    const fallbackLocale = fallback ? LOCALE_BY_LANGUAGE[fallback] : LOCALE_BY_LANGUAGE.english;
    if (fallbackLocale !== locale) setLocale(fallbackLocale);
  }, [enabledLanguages, locale]);

  const { pathname } = useLocation();

  // Adds a section's content (fetched or bundled default) to the current locale's cache.
  // A section loaded again — e.g. one shared by two pages — replaces its lists rather
  // than splicing into the previous copy.
  const addToLocaleCache = (content: Record<string, any>) => {
    setLocaleMaps((prevState) => ({
      ...prevState,
      [locale]: mergeWith({}, prevState[locale], content, replaceArraysOnMerge),
    }));
  };

  const fallbackToContentfulDefaults = (query: keyof ContentfulType) => {
    const getDefaults = DEFAULTS_BY_LOCALE[locale] || getEnglishDefaults;
    const defaults = getDefaults(query);
    setIsLoading(true);
    addToLocaleCache(defaults as Record<string, any>);
  };

  async function loadData(query: string): Promise<void> {
    const contentfulUrl = `${BACKEND_HOST}${BASE_PATH}/customers/contentful_v2/?query=${query}&locale=${locale}`;

    const res = await fetch(contentfulUrl, { method: 'GET' });

    if (res.ok) {
      const data = await res.json();

      setIsLoading(true);
      addToLocaleCache(data);
    } else if (res.status === 404) {
      // Allow 404s because many clients do not have the plan customizations section,
      // Or there _may_ be old clients missing newer section. In the former scenario,
      // not failing is the right behavior. In the latter... falling back to defaults
      // _shouldn't_ matter because the client _shouldn't_ be using that text,
      // but let's be safe until we have time to look into it.
      fallbackToContentfulDefaults(query as keyof ContentfulType);
    } else {
      Sentry.captureException(res.statusText);
      setIsError(true);
    }
  }

  // Loads data from current route
  useEffect(() => {
    const contentTypes = ROUTE_TO_QUERY_MAP[pathname];
    if (!contentTypes) return;

    // Always track the visited route so a later language switch refetches it — even when
    // the starting (bootstrap) map already had this route's text (e.g. after a login
    // redirect), in which case we skip the initial fetch below but still need the route
    // recorded. Without this, a switch would find nothing to refetch and blank the page.
    setVisited((prevState) => (prevState.includes(pathname) ? prevState : [...prevState, pathname]));

    if (!get(textMap, contentTypes[0])) {
      const promises: Promise<void>[] = [];

      for (let i = 0; i < contentTypes.length; i += 1) {
        promises.push(loadData(contentTypes[i]));
      }

      Promise.all(promises).then(() => {
        setIsLoading(false);
      });
    }
  }, [pathname, useContentfulV2]);

  useEffect(() => {
    try {
      localStorage.setItem('locale', locale);
    } catch (error) {
      console.error('Failed to save locale to localStorage:', error);
    }
  }, [locale]);

  // On a locale change, fetch any sections of the visited pages this locale hasn't cached
  // yet, then show the new language (and set <html lang> for screen readers and
  // hyphenation) once they have all settled. A failed request still lets the switch
  // finish, with that section falling back as usual.
  useEffect(() => {
    latestLocale.current = locale;
    const pending: Promise<void>[] = [];
    visited.forEach((route) => {
      const contentTypes = ROUTE_TO_QUERY_MAP[route];
      // Skip sections already cached for this locale.
      if (contentTypes && !get(localeMaps[locale], contentTypes[0])) {
        contentTypes.forEach((query) => pending.push(loadData(query)));
      }
    });

    Promise.allSettled(pending).then(() => {
      if (pending.length) setIsLoading(false);
      // A newer switch is under way; it will make its own swap.
      if (latestLocale.current !== locale) return;
      setDisplayedLocale(locale);
      document.documentElement.lang = HTML_LANG_BY_LOCALE[locale] || 'en';
    });
  }, [locale, useContentfulV2]);

  return (
    <TextContext.Provider
      value={{
        isLoading,
        textMap,
        locale,
        setLocale,
        retrieveContentfulData: (key, defaultValue?) =>
          retrieveContentfulDataFromMap(textMap, key as string, defaultValue),
        isError,
        enabledLanguages,
      }}
    >
      {children}
    </TextContext.Provider>
  );
};
